import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ConversationDetailsResponse, Message } from '@shared/types';
import apiService from '../../services/api.service';

/**
 * AdminConversationDetailPage Component
 * 
 * Detailed view of a single conversation with full message history.
 * 
 * Requirements:
 * - US3: View conversation details
 * - FR-024: Admin conversation management
 */

export const AdminConversationDetailPage: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [data, setData] = useState<ConversationDetailsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadConversation = async () => {
      if (!id) {
        navigate('/admin/dashboard');
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await apiService.get<ConversationDetailsResponse>(
          `/api/admin/conversations/${id}`
        );

        setData(response.data);
      } catch (err: any) {
        console.error('Failed to load conversation:', err);
        if (err.response?.status === 404) {
          setError(t('admin.conversationDetail.errors.notFound'));
        } else {
          setError(t('admin.conversationDetail.errors.loadFailed'));
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadConversation();
  }, [id, navigate, t]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            {t('admin.conversationDetail.loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{error}</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            {t('admin.conversationDetail.backToDashboard')}
          </button>
        </div>
      </div>
    );
  }

  const { conversation, visitor } = data;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white mb-2 transition-colors"
            >
              ← {t('admin.conversationDetail.backToDashboard')}
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              {t('admin.conversationDetail.title')}
            </h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Conversation Info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Visitor Info */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('admin.conversationDetail.visitorInfo')}
            </h2>
            <dl className="space-y-2">
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.conversationDetail.name')}
                </dt>
                <dd className="text-sm font-medium text-gray-900 dark:text-white">
                  {visitor.name}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.conversationDetail.email')}
                </dt>
                <dd className="text-sm font-medium text-gray-900 dark:text-white">
                  {visitor.email}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.conversationDetail.language')}
                </dt>
                <dd className="text-sm font-medium text-gray-900 dark:text-white">
                  {visitor.language === 'HU' ? '🇭🇺 Magyar' : '🇬🇧 English'}
                </dd>
              </div>
            </dl>
          </div>

          {/* Conversation Stats */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('admin.conversationDetail.stats')}
            </h2>
            <dl className="space-y-2">
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.conversationDetail.status')}
                </dt>
                <dd className="text-sm font-medium text-gray-900 dark:text-white">
                  {conversation.status}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.conversationDetail.progress')}
                </dt>
                <dd className="text-sm font-medium text-gray-900 dark:text-white">
                  {conversation.questionsAsked}/{conversation.totalQuestions}
                </dd>
              </div>
              <div>
                <dt className="text-sm text-gray-600 dark:text-gray-400">
                  {t('admin.conversationDetail.created')}
                </dt>
                <dd className="text-sm font-medium text-gray-900 dark:text-white">
                  {new Date(conversation.createdAt).toLocaleString()}
                </dd>
              </div>
              {conversation.completedAt && (
                <div>
                  <dt className="text-sm text-gray-600 dark:text-gray-400">
                    {t('admin.conversationDetail.completed')}
                  </dt>
                  <dd className="text-sm font-medium text-gray-900 dark:text-white">
                    {new Date(conversation.completedAt).toLocaleString()}
                  </dd>
                </div>
              )}
            </dl>
          </div>
        </div>

        {/* Message History */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t('admin.conversationDetail.messageHistory')}
            </h2>
          </div>

          <div className="p-6 space-y-4 max-h-[600px] overflow-y-auto">
            {conversation.messages.map((message: Message, index: number) => (
              <div
                key={`${message.timestamp}-${index}`}
                className={`flex ${
                  message.role === 'user' ? 'justify-end' : 'justify-start'
                }`}
              >
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">
                    {message.content}
                  </p>
                  <span
                    className={`text-xs mt-1 block ${
                      message.role === 'user'
                        ? 'text-blue-100'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {new Date(message.timestamp).toLocaleString()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};
